import React, { useMemo, useRef, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { Tooltip } from '@mui/material';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import cx from 'classnames';

import { TTimelineSession } from '../..';
import { TRow } from '../../../VideoPage';
import { useOnClickOutside } from 'usehooks-ts';
import { NOTIFICATION, VIDEOFEED } from '../../../../enums';
import EditButton from '../../../modules/EditButton';
import TrashButton from '../../../modules/TrashButton';
import styles from './TimelineRowDescription.module.scss';
import { useLongPress } from '../../../../helpers/useLongPress';
import DeleteTimelineTopicModal from '../../DeleteTimelineTopicModal';
import deleteModalStyles from '../../DeleteTimelineTopicModal/DeleteTimelineTopicModal.module.scss';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { ACCESS_TYPE_ENUM } from '../../../../enums/ACCESS_ENUM';
import Modal from '../../../modules/Modal';
import { ContainedButton, OutlinedButton } from '../../../modules/Buttons';
import { removeTopic } from '../../../../api';
import { findSearchValue } from '../../../../helpers';
import { updateNotificationTrigger, updateSecondaySelectedClips } from '../../../../store/sharedStore/sharedStoreSlice';
import { updateSortedSelectedClips } from '../../../../store/videos/videoSlice';

type TTimelineRowDescription = {
  collapsed: boolean;
  clipCount: number | string;
  row: TRow;
  nameWidth: number;
  rowNameWidth: number;
  currentFeed?: VIDEOFEED;
  selectedRows: TRow[];
  lastSelectedRow: string;
  setSession: React.Dispatch<React.SetStateAction<TTimelineSession[]>>;
  fetchData: (forJson?: boolean) => Promise<any>;
  rowIndex: number;
  onSelectingRow: (
    e: React.MouseEvent<HTMLDivElement>,
    doubleClick: boolean,
    selectedRow: TRow,
    rowIndex: number,
    selectedRowId: string,
  ) => void;
  addZeroToNumber: (value: number) => string | number;
  handleEditSubmit: (topic: TRow, newName: string) => Promise<any>;
  changedTopicIdsRef: React.MutableRefObject<{
    oldTopicId?: number;
    newTopicId?: number;
  }>;
  isMobile: boolean;
  canEdit: boolean;
  rowRef?: (node: HTMLDivElement | null) => void;
  onRowDragStart?: (rowId: string, rowIndex: number, e: React.MouseEvent<HTMLDivElement>) => void;
};

const TimelineRowDescriptionInner: React.FC<TTimelineRowDescription> = ({
  collapsed,
  clipCount,
  row,
  nameWidth,
  selectedRows,
  lastSelectedRow,
  rowIndex,
  currentFeed,
  setSession,
  fetchData,
  onSelectingRow,
  addZeroToNumber,
  rowNameWidth,
  handleEditSubmit,
  changedTopicIdsRef,
  isMobile,
  canEdit,
  rowRef,
  onRowDragStart,
}) => {
  const isNarrowRow = rowNameWidth <= 170;
  const rowContentOffset = isNarrowRow ? 20 : 0;
  const centerSelectionClassName = useMemo(
    () =>
      cx(
        styles.timelineRowContainer,
        selectedRows.some((selectedRow) => selectedRow.id === row.id)
          ? styles.selectedRowStyle
          : '',
        lastSelectedRow == row.id ? styles.selectedRowHighlight : '',
      ),
    [selectedRows, row.id, lastSelectedRow],
  );
  const [isNameEdit, setIsNameEdit] = useState(false);
  const [newTopicName, setNewTopicName] = useState('');
  const [showDotsMenu, setShowDotsMenu] = useState(false);
  const { currentUser } = useAppSelector((state) => state.user);

  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteChoiceModal, setShowDeleteChoiceModal] = useState(false);
  const [showDeleteSelectedModal, setShowDeleteSelectedModal] = useState(false);
  const [deleteSelectedLoading, setDeleteSelectedLoading] = useState(false);
  const actionMenuRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const selectionLockRef = useRef<{ userSelect: string; webkitUserSelect: string } | null>(null);
  const dragStartRef = useRef<{
    startY: number;
    rowId: string;
    rowIndex: number;
    dragging: boolean;
  } | null>(null);
  const dispatch = useAppDispatch();
  const feedId = findSearchValue('feedId');

  const handleDeleteSelectedRows = async (e: any) => {
    e?.stopPropagation();
    if (!feedId) return;
    setDeleteSelectedLoading(true);
    try {
      const ids = selectedRows.map((selectedRow) => Number(selectedRow.id));
      const { data } = await removeTopic({ id: ids }, +feedId);
      const deletedTopics = Array.isArray(data) ? data : data ? [data] : [];
      if (deletedTopics.length) {
        await fetchData(true);
        setSession((prev) => [
          ...prev,
          ...deletedTopics.map(({ id, name, topicIndex, clips, topic_details }) => ({
            topic: {
              id,
              name,
              topicIndex,
              clips: [...clips],
              topicDetails: topic_details,
              status: 'deleted',
            },
          })),
        ]);
      }
      dispatch(updateNotificationTrigger(Date.now()));
    } catch (err: any) {
      console.log(err);
      enqueueSnackbar(err?.response?.data?.message || 'Please try again', { variant: 'error' });
    } finally {
      setShowDeleteSelectedModal(false);
      dispatch(updateSecondaySelectedClips([]));
      dispatch(updateSortedSelectedClips([]));
      setDeleteSelectedLoading(false);
    }
  };

  const handleNameEdit = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canEdit) return;

    setShowDotsMenu(false);
    setNewTopicName(row.name);
    setIsNameEdit(true);

    setTimeout(() => {
      if (inputRef && inputRef?.current) {
        inputRef.current.focus();
      }
    }, 150);
  };

  const longPressHandlers = useLongPress<HTMLDivElement>(handleNameEdit, 800);

  const handleRowMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const isOptionHeld = e.altKey;
    if (isOptionHeld) {
      // Reorder mode: do not trigger long-press rename while Option is held
      longPressHandlers.onMouseUp();
    } else {
      longPressHandlers.onMouseDown(e);
    }

    if (!canEdit || !onRowDragStart) return;
    if (!isOptionHeld) return;
    if (e.metaKey || e.ctrlKey) return;
    e.preventDefault();
    e.stopPropagation();

    const bodyStyle = document.body.style as CSSStyleDeclaration & { webkitUserSelect?: string };
    selectionLockRef.current = {
      userSelect: bodyStyle.userSelect || '',
      webkitUserSelect: bodyStyle.webkitUserSelect || '',
    };
    bodyStyle.userSelect = 'none';
    bodyStyle.webkitUserSelect = 'none';

    dragStartRef.current = {
      startY: e.clientY,
      rowId: row.id,
      rowIndex,
      dragging: false,
    };

    const handleMouseMove = (event: MouseEvent) => {
      if (!dragStartRef.current) return;
      // Require Option to stay held while initiating reorder.
      if (!event.altKey) return;
      const deltaY = Math.abs(event.clientY - dragStartRef.current.startY);
      if (deltaY >= 30 && !dragStartRef.current.dragging) {
        dragStartRef.current.dragging = true;
        onRowDragStart(dragStartRef.current.rowId, dragStartRef.current.rowIndex, e);
      }
    };

    const handleMouseUp = () => {
      dragStartRef.current = null;
      const bodyStyle = document.body.style as CSSStyleDeclaration & { webkitUserSelect?: string };
      if (selectionLockRef.current) {
        bodyStyle.userSelect = selectionLockRef.current.userSelect;
        bodyStyle.webkitUserSelect = selectionLockRef.current.webkitUserSelect;
        selectionLockRef.current = null;
      }
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const handleRowMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
    longPressHandlers.onMouseUp();
  };

  const handleRowMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    longPressHandlers.onMouseLeave();
  };

  const onNameSubmit = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (row.name.trim() !== newTopicName.trim() && isNameEdit) {
      if (!newTopicName.trim()) {
        setNewTopicName(row.name);
        setTimeout(() => {
          enqueueSnackbar(NOTIFICATION.EMPTY, {
            variant: 'warning',
            anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
          });
        }, 100);
        return;
      }

      handleEditSubmit(row, newTopicName);
    }
    onEditClose();
  };

  const onEditClose = () => {
    setShowDotsMenu(false);
    setIsNameEdit(false);
    setNewTopicName('');
  };

  const onNameEdit = (e: any) => {
    setShowDotsMenu(false);
    setNewTopicName(e.target.value);
  };

  const switchDotsMenuContext = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (
      !canEdit &&
      currentFeed &&
      currentFeed?.created_by?.id !== currentUser?.id &&
      currentFeed?.highest_user_access?.access?.title !== ACCESS_TYPE_ENUM.EDIT_AND_RE_SHARE
    ) {
      return;
    }

    setShowDotsMenu(true);

    // get menu and viewport dimensions
    const menuW = actionMenuRef.current?.offsetWidth ?? 100;
    const menuH = actionMenuRef.current?.offsetHeight ?? 85;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // start from click point
    let x = e.clientX;
    let y = e.clientY;

    // clamp right/bottom
    if (x + menuW > vw) x = vw - menuW - 10;
    if (y + menuH > vh) y = vh - menuH - 10;

    // clamp left/top
    if (x < 10) x = 10;
    if (y < 10) y = 10;

    const left = x + 20;
    setMenuPosition({ x: left, y });
  };

  useOnClickOutside(inputRef, (e) => {
    // e.stopPropagation();
    onNameSubmit(e);
  });

  useOnClickOutside(actionMenuRef, (e) => {
    e.stopPropagation();
    setShowDotsMenu(false);
  });

  return (
    <>
      <div
        onMouseDown={handleRowMouseDown}
        onMouseUp={handleRowMouseUp}
        onMouseLeave={handleRowMouseLeave}
        onTouchStart={longPressHandlers.onTouchStart}
        onTouchEnd={longPressHandlers.onTouchEnd}
        ref={rowRef}
        className={cx(styles.timelineSidebarRow, collapsed && styles.collapsed)}
        key={`${row.name}_${rowIndex}_${row.clips?.length}`}
        id={`${row.name}_${rowIndex}_${row.clips?.length}`}
        onDragStart={(e) => e.preventDefault()}
        onClick={(e) => onSelectingRow(e, false, row, rowIndex, row.id)}
        onDoubleClick={(e) => onSelectingRow(e, true, row, rowIndex, row.id)}
        onContextMenu={switchDotsMenuContext}
      >
        {!isMobile && <div className={styles.timelineSidebarRowIndex}>{addZeroToNumber(rowIndex + 1)}</div>}
        {isMobile && canEdit && (
          <div className={styles.mobileRowDragHandle} aria-hidden="true">
            <DragIndicatorRoundedIcon />
          </div>
        )}
        <div className={centerSelectionClassName} style={{ width: `${Math.max(0, rowNameWidth - rowContentOffset)}px` }}>
          {isNameEdit ? (
            <form
              onClick={(e) => {
                e.stopPropagation();
              }}
              onSubmit={onNameSubmit}
            >
              <div className={styles.inputWrapper}>
                <input
                  className={styles.editText}
                  onChange={onNameEdit}
                  value={newTopicName}
                  ref={inputRef}
                  onKeyDownCapture={(e) => e.stopPropagation()}
                  onKeyUpCapture={(e) => e.stopPropagation()}
                  onKeyPressCapture={(e) => e.stopPropagation()}
                />
              </div>
            </form>
          ) : row.name?.length > 16 ? (
            <Tooltip title={row.name} placement="right">
              <div className={styles.timelineSidebarRowName} style={{ width: `${nameWidth}px` }}>
                {row.name}
              </div>
            </Tooltip>
          ) : (
            <div className={styles.timelineSidebarRowName} style={{ width: `${nameWidth}px` }}>
              {row.name}
            </div>
          )}
          {isMobile && <ChevronRightRoundedIcon className={styles.mobileRowChevron} />}
        </div>
        <div className={styles.timelineSidebarRowClips}>
          {isMobile ? <span className={styles.timelineSidebarRowClipCount}>{clipCount}</span> : clipCount}
          <div
            ref={actionMenuRef}
            className={styles.actionMenu}
            style={{
              display: showDotsMenu ? 'block' : 'none',
              top: `-${menuPosition.y}px`,
            }}
          >
            <div className={styles.menuOption} onClick={handleNameEdit}>
              <EditButton /> Rename
            </div>
            <div
              className={styles.menuOption}
              onClick={(e) => {
                e?.stopPropagation();
                const isRowSelected = selectedRows.some((selectedRow) => selectedRow.id === row.id);
                if (isRowSelected && selectedRows.length > 1) {
                  setShowDeleteChoiceModal(true);
                  setShowDotsMenu(false);
                  return;
                }
                setShowDeleteModal(!showDeleteModal);
                setShowDotsMenu(false);
              }}
            >
              <TrashButton /> Delete
            </div>
          </div>
        </div>
        <DeleteTimelineTopicModal
          open={showDeleteModal}
          setSession={setSession}
          fetchData={fetchData}
          switchModal={() => setShowDeleteModal(!showDeleteModal)}
          row={row}
          changedTopicIdsRef={changedTopicIdsRef}
        />
        <Modal open={showDeleteChoiceModal} onClose={() => setShowDeleteChoiceModal(false)} loading={false}>
          <div className={deleteModalStyles.modalBody} onClick={(e) => e.stopPropagation()}>
            <div className={deleteModalStyles.modalTitle}>Delete current row or all selected rows?</div>
            <div className={deleteModalStyles.actionBtns}>
              <OutlinedButton
                onClick={(e) => {
                  e?.stopPropagation();
                  setShowDeleteChoiceModal(false);
                  setShowDeleteModal(!showDeleteModal);
                  setShowDotsMenu(false);
                }}
                kind="default"
              >
                Delete Row {row.name}
              </OutlinedButton>

              <ContainedButton
                kind="purple"
                onClick={(e) => {
                  e?.stopPropagation();
                  setShowDeleteChoiceModal(false);
                  setShowDeleteSelectedModal(true);
                }}
              >
                Delete All Selected Rows
              </ContainedButton>
            </div>
          </div>
        </Modal>
        <Modal open={showDeleteSelectedModal} onClose={() => setShowDeleteSelectedModal(false)} loading={deleteSelectedLoading}>
          <div className={deleteModalStyles.modalBody} onClick={(e) => e.stopPropagation()}>
            <div className={deleteModalStyles.modalTitle}>Are you sure you want to delete all selected rows?</div>
            <div className={deleteModalStyles.namesListForTopicDeletion}>
              {selectedRows.map((selectedRow) => (
                <div key={`delete_selected_row_${selectedRow.id}`}>{selectedRow.name}</div>
              ))}
            </div>
            <div className={deleteModalStyles.actionBtns}>
              <OutlinedButton kind="default" disabled={deleteSelectedLoading} onClick={handleDeleteSelectedRows}>
                Delete All Selected Rows
              </OutlinedButton>

              <ContainedButton
                kind="purple"
                disabled={deleteSelectedLoading}
                onClick={(e) => {
                  e?.stopPropagation();
                  setShowDeleteSelectedModal(false);
                }}
              >
                Cancel
              </ContainedButton>
            </div>
          </div>
        </Modal>
      </div>
    </>
  );
};

export const TimelineRowDescription = React.memo(
  TimelineRowDescriptionInner,
  (prev, next) =>
    prev.row === next.row &&
    prev.clipCount === next.clipCount &&
    prev.nameWidth === next.nameWidth &&
    prev.rowNameWidth === next.rowNameWidth &&
    prev.currentFeed === next.currentFeed &&
    prev.selectedRows === next.selectedRows &&
    prev.lastSelectedRow === next.lastSelectedRow &&
    prev.onSelectingRow === next.onSelectingRow &&
    prev.rowIndex === next.rowIndex &&
    prev.isMobile === next.isMobile &&
    prev.canEdit === next.canEdit &&
    prev.collapsed === next.collapsed,
) as React.FC<TTimelineRowDescription>;

TimelineRowDescription.displayName = 'TimelineRowDescription';
